/**
 * Clinic assignment helpers for role-scoped new-case forms.
 * ClinicManagers (including assistant managers resolved as CM) may be assigned
 * one or more clinics via TherapieMaps.
 */

/** TherapieMaps `country` values — must match roleSchema.COUNTRY_GROUP_TM_COUNTRY. */
export const CLINIC_COUNTRY_GROUP = Object.freeze({
  IOI: 'Ireland',
  GB:  'United Kingdom',
  US:  'United States',
});

export function getAssignedClinicNames(user, tmData = null) {
  return resolveCmClinicObjects(user, tmData)
    .map(c => (c.name || '').trim())
    .filter(Boolean);
}

/**
 * CM/AM clinic rows from every source the server exposes — user profile,
 * /reference/clinics assignment, or the staff index. Auth often has an empty
 * clinicObjects[] even when assignment is populated.
 */
export function resolveCmClinicObjects(user, tmData = null) {
  if (!user || user.role !== 'ClinicManager') return [];

  // Base clinics from the first populated source (priority chain unchanged).
  let base = [];
  if (user.clinicObjects?.length) {
    base = user.clinicObjects;
  } else if (tmData?.assignment?.clinicObjects?.length) {
    base = tmData.assignment.clinicObjects;
  } else {
    const email = (user.email || '').toLowerCase();
    const staff = tmData?.cmByMail?.[email] || tmData?.amByMail?.[email];
    if (staff?.clinicObjects?.length) {
      base = staff.clinicObjects;
    } else {
      const scopeNames = tmData?.scope?.clinics || [];
      if (scopeNames.length && tmData?.clinicByName) {
        base = scopeNames
          .map(n => tmData.clinicByName[String(n).toLowerCase()])
          .filter(Boolean);
      }
    }
  }

  // UNION IN temporary clinic-access grants (v0.38.0) exposed as
  // tmData.scope.extraClinics (a name list). Base clinics stay FIRST so
  // defaultAssignedClinicName() keeps returning the CM's primary clinic; a grant
  // already present in base is not duplicated. No extraClinics → byte-identical
  // to the pre-grant behaviour (regression-safe).
  const extraNames = tmData?.scope?.extraClinics || [];
  if (extraNames.length && tmData?.clinicByName) {
    const seen = new Set(base.map(c => (c?.name || '').trim().toLowerCase()).filter(Boolean));
    const granted = extraNames
      .map(n => tmData.clinicByName[String(n).toLowerCase()])
      .filter(Boolean)
      .filter(c => {
        const key = (c.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    if (granted.length) return [...base, ...granted];
  }
  return base;
}

/** Full clinic records from TherapieMaps matching the user's assignment. */
export function getAssignedClinics(user, allClinics, tmData = null) {
  const objects = resolveCmClinicObjects(user, tmData);
  if (!objects.length) return [];
  if (!Array.isArray(allClinics) || !allClinics.length) {
    return objects.filter(c => c?.name);
  }

  const byName = new Set(
    objects.map(c => (c.name || '').trim().toLowerCase()).filter(Boolean),
  );
  const byEmail = new Set(
    objects.map(c => (c.email || '').trim().toLowerCase()).filter(Boolean),
  );

  const matched = allClinics.filter(c =>
    byName.has((c.name || '').toLowerCase())
    || (c.email && byEmail.has(c.email.toLowerCase())),
  );
  return matched.length ? matched : objects.filter(c => c?.name);
}

export function isSingleClinicManager(user, tmData = null) {
  return user?.role === 'ClinicManager' && resolveCmClinicObjects(user, tmData).length === 1;
}

export function isMultiClinicManager(user, tmData = null) {
  return user?.role === 'ClinicManager' && resolveCmClinicObjects(user, tmData).length > 1;
}

/** First assigned clinic — default for new submissions. */
export function defaultAssignedClinicName(user, tmData = null) {
  return getAssignedClinicNames(user, tmData)[0] || '';
}

/** Map a TherapieMaps clinic row to its country group (null when country is unknown). */
export function clinicCountryGroup(clinic) {
  const country = String(clinic?.country || '').trim();
  if (country === CLINIC_COUNTRY_GROUP.IOI) return 'IOI';
  if (country === CLINIC_COUNTRY_GROUP.GB) return 'GB';
  if (country === CLINIC_COUNTRY_GROUP.US) return 'US';
  return null;
}

/** Which country groups appear in a CM's assigned-clinic list. `spansMultiple`
 *  generalises the old two-group `spansBoth` now that there are 3+ groups. */
export function assignedClinicGroups(assignedClinics) {
  const groups = new Set(
    (assignedClinics || []).map(clinicCountryGroup).filter(Boolean),
  );
  return {
    hasIoi:        groups.has('IOI'),
    hasGb:         groups.has('GB'),
    hasUs:         groups.has('US'),
    groups:        [...groups],
    spansMultiple: groups.size > 1,
  };
}

export function clinicMatchesCountryFilter(clinic, filter) {
  return clinicCountryGroup(clinic) === filter;
}

/**
 * The clinic manager to record for a freshly selected clinic.
 *
 * ALWAYS returns a value, including '' — the caller must assign it unconditionally.
 * Assigning only when a CM was found is what let a previous clinic's manager ride
 * along: picking Bath filled in its CM, switching to a clinic with no CM in Maps
 * updated Region and Country but left the old name, and the case was filed against
 * the wrong manager (AE-2066, 2026-09-17).
 *
 * A Clinic Manager filing their own case keeps their own name — the field is
 * read-only for them and their name is the right answer whatever Maps knows.
 *
 * @param {Array} cmsForClinic  CMs designated to the selected clinic (may be empty)
 * @param {object} user         the signed-in user
 * @returns {string}
 */
export function resolveClinicManagerName(cmsForClinic, user = null) {
  const cm = (Array.isArray(cmsForClinic) ? cmsForClinic : [])[0];
  const fromMaps = cm?.displayName || cm?.name || '';
  if (fromMaps) return fromMaps;
  return user?.role === 'ClinicManager' ? (user.name || '') : '';
}
